import * as React from 'react'
import {GeometryType} from '../../enums/GeometryType'
import {IDataQueriesDispatcher, ILayersDispatcher} from '../../interfaces/dispatchers'
import {IFeatureLayerInfo, IHiddenGroupCaches, IImageLegendRowInfo} from '../../interfaces/models'
import {IDataQueries, IFeatureLayerInfos} from '../../interfaces/reducers'
import {config} from '../../reducers/config'
import OverlayLoader from '../Loading/OverlayLoading'

export interface Props {
  visibleLayers: {[key: string]: any}
  layerInfos: IFeatureLayerInfos
  layersDispatcher: ILayersDispatcher
  hiddenGroupsCaches: IHiddenGroupCaches
  layerListWithLegend?: boolean
  zoom: number
  sourcesImported: boolean
  vectorLayerImagesLoaded: boolean
  dataQueryDispatcher: IDataQueriesDispatcher
  dataQueries: IDataQueries
}

interface State {
  floor: number
  folded: {[groupName: string]: boolean}
}

class Legend extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      floor: 0,
      folded: {
        common: true,
        oup: true,
        und: true,
        zones: true,
        zonelines: true,
        cad: true,
        streetreg: true,
        Containers: true,
        velo: true,
        parkings: true,
        healthFacilities: true,
        repairs: true,
        events: true,
        archimport: true,
        properties: true,
        misc: true,
        crosses: true
      }
    }
  }

  componentDidMount(): void {
    // Skip initialization if groups are already set (remount after navigation/tab switch).
    // Redux state persists, so re-initializing would overwrite the user's saved visibility.
    if (Object.keys(this.props.hiddenGroupsCaches).length > 0) {
      return
    }
    const {layersDispatcher, layerInfos} = this.props
    type GroupKeys =
      | 'cad'
      | 'zones'
      | 'zonelines'
      | 'streetreg'
      | 'oup'
      | 'und'
      | 'archimport'
      | 'common'
      | 'properties'
      | 'misc'
      | 'crosses'
    const initUnchechecked: Record<GroupKeys, Record<string, string>> = {
      cad: {},
      zones: {},
      zonelines: {},
      streetreg: {},
      oup: {},
      und: {},
      archimport: {},
      common: {},
      properties: {},
      misc: {},
      crosses: {}
    }
    for (const key in layerInfos) {
      if (initUnchechecked[layerInfos[key].sourceDescription.group as GroupKeys]) {
        initUnchechecked[layerInfos[key].sourceDescription.group as GroupKeys][layerInfos[key].url] =
          layerInfos[key].url
      }
    }
    layersDispatcher.setHiddenGroupCaches(initUnchechecked)
  }

  getGroupDisplayName(groupName: string): string {
    const conf = config.getConfig()
    if (conf.groupAliases && conf.groupAliases[conf.locale] && conf.groupAliases[conf.locale][groupName]) {
      return conf.groupAliases[conf.locale][groupName]
    }
    return groupName
  }

  getGroupControl(groupName: string, layerInfos: IFeatureLayerInfos, withChecks: boolean): JSX.Element {
    return (
      <div className="heading">
        <div className="details">
          {withChecks && (
            <input
              className="regular-checkbox"
              style={{transform: 'scale(1.4)', marginRight: '10px'}}
              type="checkbox"
              checked={!this.props.hiddenGroupsCaches[groupName]}
              onChange={() => {
                const hiddenGroupsCaches = {...this.props.hiddenGroupsCaches}
                const folder = {...this.state.folded}
                if (this.props.hiddenGroupsCaches[groupName]) {
                  delete hiddenGroupsCaches[groupName]
                  delete folder[groupName]
                  this.props.layersDispatcher.setHiddenGroupCaches(hiddenGroupsCaches)
                } else {
                  folder[groupName] = true
                  delete hiddenGroupsCaches[groupName]
                  this.props.layersDispatcher.setHiddenGroupCaches({...hiddenGroupsCaches, [groupName]: {}})
                }
                this.setState({...this.state, folded: folder})
              }}
            />
          )}
          <div
            style={{
              color: this.props.hiddenGroupsCaches[groupName] ? this.notVisibleTextColor : '#0d3058'
            }}
          >
            {this.getGroupDisplayName(groupName)}
          </div>
        </div>
      </div>
    )
  }

  isLayerVisible(layerInfo: IFeatureLayerInfo): boolean {
    const hiddenGroups = {...this.props.hiddenGroupsCaches}
    const hiddenGroup = hiddenGroups[layerInfo.sourceDescription.group]
    let isVisible = !!this.props.visibleLayers[layerInfo.url]
    if (hiddenGroup) {
      isVisible = false
    }
    return (
      isVisible &&
      !this.props.hiddenGroupsCaches[layerInfo.sourceDescription.group] &&
      !!layerInfo.getLayer(this.props.zoom)
    )
  }
  notVisibleTextColor = '#B9BCC3'
  renderLegendRowInfos(
    layerInfo: IFeatureLayerInfo,
    labelFirst: boolean,
    imageLegendRows: IImageLegendRowInfo[]
  ): JSX.Element {
    const isVisible = this.isLayerVisible(layerInfo)
    if (imageLegendRows.length > 1) {
      const arr: JSX.Element[] = []
      imageLegendRows.forEach((r, i) => {
        arr.push(
          <div
            key={i}
            className={`layer ${isVisible ? 'visible' : ''}`}
            style={{backgroundImage: `url(${r.base64})`, backgroundSize: '40px'}}
          >
            {r.label}
          </div>
        )
      })
      return <div style={{marginLeft: '32px'}}>{arr}</div>
    } else if (imageLegendRows.length == 1) {
      const r = imageLegendRows[0]
      return (
        <img
          alt=""
          style={{
            height: r.height ? `${r.height}px` : '',
            width: r.width ? `${r.width}px` : '',
            opacity: isVisible ? 1 : 0.5
          }}
          src={r.base64}
        />
      )
    }
    return <div></div>
  }

  getLegendRowSymbology(layerInfo: IFeatureLayerInfo, labelFirst: boolean) {
    let symbol: JSX.Element | null = (
      <div style={{width: '30px', height: '30px', position: 'relative', marginRight: '5px'}}>
        <OverlayLoader size={'20px'} show={true}></OverlayLoader>
      </div>
    )
    const imageLegendRows = this.getImages(layerInfo)
    if (imageLegendRows.length > 0) {
      symbol = this.renderLegendRowInfos(layerInfo, labelFirst, imageLegendRows)
    }

    return symbol
  }

  getLegendRowTitle(layerInfo: IFeatureLayerInfo) {
    return <div>{layerInfo.sourceDescription.sourceTitle}</div>
  }

  getLegendRow(layerInfo: IFeatureLayerInfo) {
    let rowStyle: React.CSSProperties = {display: 'flex', alignItems: 'center', justifyContent: 'space-between'}
    if (this.getImages(layerInfo).length > 1) {
      rowStyle = {textAlign: 'left'}
    }
    return (
      <div key={layerInfo.url} className="legend-row" style={rowStyle}>
        {this.getLegendRowTitle(layerInfo)}
        {layerInfo.getGeometryType() != GeometryType.None && layerInfo.getLayer(this.props.zoom)
          ? this.getLegendRowSymbology(layerInfo, true)
          : null}
      </div>
    )
  }

  protected processRow(info: IFeatureLayerInfo): boolean {
    return info.getGeometryType() != GeometryType.None && !info.sourceDescription.noLegend
  }

  getLegendRows(layerInfos: IFeatureLayerInfos): Array<JSX.Element> {
    const result: Array<JSX.Element> = []
    for (const url in layerInfos) {
      if (this.props.visibleLayers[layerInfos[url].sourceDescription.id] && this.processRow(layerInfos[url])) {
        result.push(this.getLegendRow(layerInfos[url]))
      }
    }
    return result
  }

  getGroupedLayers(groupName: string, layerInfos: IFeatureLayerInfos, withVisibilityControl: boolean): JSX.Element {
    return (
      <div key={groupName} style={{display: 'flex', flexDirection: 'column', margin: '5px'}}>
        {groupName ? this.getGroupControl(groupName, layerInfos, withVisibilityControl) : null}
        <div key={groupName} style={{display: 'flex', flexDirection: 'column', marginLeft: groupName ? '30px' : ''}}>
          {this.getLegendRows(layerInfos)}
        </div>
      </div>
    )
  }

  getLayerGroups(withVisibilityControl: boolean): JSX.Element[] {
    const groupLayers: {[groupName: string]: IFeatureLayerInfos} = {}
    const groups: JSX.Element[] = []
    for (const key in this.props.layerInfos) {
      const layerInfo = this.props.layerInfos[key]
      if (layerInfo.getGeometryType() != GeometryType.None) {
        if (!groupLayers[layerInfo.sourceDescription.group]) {
          groupLayers[layerInfo.sourceDescription.group] = {}
        }
        groupLayers[layerInfo.sourceDescription.group][layerInfo.url] = layerInfo
      }
    }
    for (const group in groupLayers) {
      if (!withVisibilityControl && this.props.hiddenGroupsCaches[group]) {
        continue
      }
      groups.push(this.getGroupedLayers(group, groupLayers[group], withVisibilityControl))
    }
    return groups
  }

  getImages(layerInfo: IFeatureLayerInfo): IImageLegendRowInfo[] {
    const imageLegendRows = layerInfo.imageLegendRows
    return imageLegendRows
  }

  render() {
    return <div>{this.getLayerGroups(false)}</div>
  }
}

export default Legend
